import React from "react";
import { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";

import { BaseProcedureType } from "@graffer-inc/cloud-gov-core";
import {
  ReactSelect,
  ReactSelectOption,
  TextField,
} from "@graffer-inc/console-design-kit";
import {
  CreateKintoneExportSettingInput,
  reportUnexpectedError,
} from "@graffer-inc/smart-apply";

import apiClient from "../../../../../application/apis/api-client";
import {
  validateDescription,
  validateName,
} from "../../../../../application/domain/kintone-export";
import { useAuthContextV2 } from "../../../../../auth-state";
import { useInputProps } from "../../common-hook";
import { useUrlInputProps } from "./hook";

export type Props = {
  createKintoneExportSettingInput: CreateKintoneExportSettingInput;
  onChange: (
    createKintoneExportSettingInput: CreateKintoneExportSettingInput,
  ) => void;
};

export const KintoneExportSettingCreateForm: React.FC<Props> = ({
  createKintoneExportSettingInput,
  onChange,
}) => {
  const nameInputProps = useInputProps({
    value: createKintoneExportSettingInput.name,
    onChange: (v) => {
      onChange({
        ...createKintoneExportSettingInput,
        name: v,
      });
    },
    validate: validateName,
  });

  const descriptionInputProps = useInputProps({
    value: createKintoneExportSettingInput.description,
    onChange: (v) => {
      onChange({
        ...createKintoneExportSettingInput,
        description: v,
      });
    },
    validate: validateDescription,
  });

  const urlInputProps = useUrlInputProps({
    param: createKintoneExportSettingInput,
    onChange: (v) => {
      onChange({
        ...createKintoneExportSettingInput,
        ...v,
      });
    },
  });

  const apiTokenInputProps = useInputProps({
    value: createKintoneExportSettingInput.kintoneAPIToken,
    onChange: (v) => {
      onChange({
        ...createKintoneExportSettingInput,
        kintoneAPIToken: v,
      });
    },
    validate: (v) => {
      if (!v) {
        return "kintone API トークンは必ず入力してください。";
      }
      return undefined;
    },
  });

  return (
    <div>
      <div className="columns">
        <div className="column">
          <p>
            「スマート申請手続き」、「kintone アプリ
            URL」は、あとから編集できません。
          </p>
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="設定名称"
            required
            placeholder="設定名称"
            description={"256文字以内で入力してください。"}
            {...nameInputProps}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="説明文"
            placeholder="説明文を入力してください"
            description={"256文字以内で入力してください。"}
            {...descriptionInputProps}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <ProcedureSelect
            tenantId={createKintoneExportSettingInput.tenantId}
            onChange={(v) => {
              onChange({
                ...createKintoneExportSettingInput,
                formResourceId: v,
              });
            }}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="kintone アプリ URL"
            required
            placeholder="kintone アプリ URL"
            description={
              "kintone アプリの URL を入力してください。\n例: https://graffer.cybozu.com/k/1000/"
            }
            {...urlInputProps}
          />
        </div>
      </div>

      <div className="columns">
        <div className="column">
          <TextField
            label="kintone API トークン"
            required
            placeholder="kintone API トークン"
            description={
              <>
                kintone API トークンを入力してください。
                <br />
                トークンの生成方法は
                <a
                  href="https://jp.cybozu.help/k/ja/user/app_settings/api_token.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  kintone 公式ページ
                </a>
                を参照してください。
                <br />
                トークンへは「レコード閲覧」「レコード追加」のアクセス権を付与してください。
                <br />
                トークンは後から変更することができます。
              </>
            }
            {...apiTokenInputProps}
          />
        </div>
      </div>
    </div>
  );
};

const ProcedureSelect: React.FC<{
  tenantId: string;
  onChange: (formResourceId: string) => void;
}> = ({ tenantId, onChange }) => {
  const [isLoading, setLoading] = React.useState(false);
  const [procedures, setProcedures] = React.useState<BaseProcedureType[]>([]);
  const [selected, setSelected] = React.useState<{
    value: string;
    label: string;
  }>();

  const { auth } = useAuthContextV2();

  const searchProcedures = useDebouncedCallback(async (text = "") => {
    setLoading(true);
    const limit = 50;

    try {
      const query: Parameters<typeof apiClient.fetchProcedures> = [
        1,
        limit,
        {
          name: text,
          // 下書き〜運用中のものに限る
          publishStatus: [
            "DRAFT",
            "BEFORE_ACCEPTING",
            "ACCEPTING",
            "PAUSING_ACCEPT",
          ],
        },
      ];
      const { data } = auth.isServiceProviderAccount()
        ? await apiClient.fetchProceduresByOrgId(tenantId!, ...query)
        : await apiClient.fetchProcedures(...query);
      setProcedures(data);
    } catch (error) {
      reportUnexpectedError(error);
      toast.error("手続きの検索に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, 1000);

  const candidates = React.useMemo<{ value: string; label: string }[]>(() => {
    return procedures.map((proc) => {
      return {
        value: proc.id,
        label: `${proc.name} (ID: ${proc.id})`,
      };
    });
  }, [selected, procedures]);

  const [touched, setTouched] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState<string>();

  React.useEffect(() => {
    if (!touched) {
      return;
    }
    if (!selected) {
      setErrorMessages("手続きを選択してください。");
      return;
    }
  }, [touched, setErrorMessages, selected]);

  return (
    <ReactSelect
      required
      isClearable
      label="スマート申請手続き"
      description={
        <>
          連携元となるスマート申請の手続きを選択してください。
          <br />
          kintoneに連携が失敗した場合、手続きに割り当てられた管理グループのユーザーにメール通知されます。
          <br />
          メールアドレスが設定されていない場合、失敗を検知できないため登録しておくことを推奨します。
        </>
      }
      placeholder={"手続き名から検索"}
      value={selected}
      onInputChange={(text) => {
        // 型からは読み取れないが、ここで promise を返すと
        // バグが起きるので、あえて即時関数を生成している。
        // （非同期ではない、返却が何もない関数にしている）
        searchProcedures(text);
      }}
      onChange={(v) => {
        setTouched(true);
        const newValue = v as SingleValue<ReactSelectOption<string>>;
        if (newValue) {
          setSelected(newValue);
          onChange(newValue.value);
          setErrorMessages(undefined);
        } else {
          setSelected(undefined);
          onChange("");
        }
      }}
      options={candidates}
      isLoading={isLoading}
      loadingMessage={() => "検索中..."}
      noOptionsMessage={() => "手続きがありません"}
      errorMessages={errorMessages}
      onBlur={() => {
        setTouched(true);
      }}
    />
  );
};
